import React, { ComponentProps, forwardRef, memo } from 'react'

import styled from '@emotion/styled'

import { Colors, FontSizes } from '@surglogs-app/ui.theme'

import {
  TypographyColorVariant,
  getColorKeyFromColorVariant,
} from '../services/TypographyService'
import {
  StyleProps,
  getCSSObjectFromStyleProps,
} from '../styleProps/StylePropsService'

export type TitleLevel = 1 | 2 | 3 | 4 | 5 | 6
export type TitleSize = FontSizes

export interface TitleProps
  extends Omit<ComponentProps<typeof StyledTitle>, 'variant' | 'size'> {
  variant?: TypographyColorVariant
  size?: TitleSize
  /** Determines which tag will be used (h1-h6), `2` by default */
  level?: TitleLevel
}

const StyledTitle = styled.h2<
  { variant: Colors; size: TitleSize } & StyleProps
>`
  font-family: ${({ theme }) => theme.ui.typography.fontFamilies.default};
  font-size: ${({ theme, size }) => theme.ui.typography.fontSizes[size]};
  line-height: ${({ theme, size }) => theme.ui.typography.lineHeights[size]};
  font-weight: ${({ theme }) => theme.ui.typography.fontWeights.semiBold};
  color: ${({ theme, variant }) => theme.ui.colors[variant]};

  ${getCSSObjectFromStyleProps}
`

export const Title = memo(
  forwardRef<HTMLHeadingElement, TitleProps>(
    ({ variant = 'default', size = 'lg', level = 2, ...props }, ref) => (
      <StyledTitle
        {...props}
        {...{
          as: `h${level}`,
          ref,
          size,
          variant: getColorKeyFromColorVariant(variant),
        }}
      />
    ),
  ),
)
